/**
 * RecommendationsLoadingState
 *
 * Косметичний loading state для /recommendations: skeleton-картки знизу +
 * центральний прогрес з ротуючими повідомленнями зверху. Юзеру стає видно
 * що сторінка завантажується, і він розуміє ЩО саме зараз відбувається —
 * fetch у Google Ads це не миттєва справа (~3-5 сек на API + ~5-8 сек AI).
 *
 * Дизайн натхненний LinkedIn/YouTube loading patterns.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";

// Ротація стадій — ~2.5 сек кожна. Покриває cumulative timing fetch+AI+cache.
const LOADING_STAGES = [
    { emoji: "🔌", text: "Підключаюсь до Google Ads…" },
    { emoji: "📥", text: "Тягну активні рекомендації…" },
    { emoji: "🧠", text: "AI аналізує під твою стратегію…" },
    { emoji: "⚖️",  text: "Зважую impact для твого бюджету…" },
    { emoji: "✨", text: "Майже готово, останні штрихи…" },
];

const SkeletonCard = ({ delay = 0 }) => (
    <div
        className="rounded-xl border border-white/5 bg-surface p-4 flex items-center gap-4 animate-pulse"
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className="w-10 h-10 rounded-lg bg-zinc-700/40 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2.5">
            <div className="flex items-center gap-3">
                <div className="h-4 bg-zinc-700/40 rounded w-44" />
                <div className="h-5 bg-zinc-700/30 rounded-full w-24" />
            </div>
            <div className="h-3 bg-zinc-700/30 rounded w-3/4" />
        </div>
        <div className="w-5 h-5 rounded bg-zinc-700/30 shrink-0" />
    </div>
);

const RecommendationsLoadingState = () => {
    const [stageIdx, setStageIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => {
            setStageIdx((i) => (i + 1) % LOADING_STAGES.length);
        }, 2500);
        return () => clearInterval(t);
    }, []);

    const stage = LOADING_STAGES[stageIdx];

    return (
        <div className="space-y-6">
            {/* Hero status block */}
            <div className="rounded-2xl border border-white/5 bg-surface p-6 sm:p-8 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(110deg, transparent 30%, rgba(168, 130, 255, 0.15) 50%, transparent 70%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 2.5s ease-in-out infinite",
                    }}
                />

                <div className="relative flex items-start gap-4">
                    <div className="relative shrink-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-primary-c animate-pulse" />
                        </div>
                        <Loader2 className="w-4 h-4 text-primary-c absolute -bottom-1 -right-1 animate-spin" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <h2 className="text-base sm:text-lg font-semibold text-text-primary">
                            Готуємо рекомендації для тебе
                        </h2>

                        <div
                            key={stageIdx}
                            className="mt-2 flex items-center gap-2 text-sm text-text-secondary"
                            style={{ animation: "fadeInUp 0.4s ease-out" }}
                        >
                            <span className="text-base" aria-hidden>{stage.emoji}</span>
                            <span>{stage.text}</span>
                        </div>

                        <p className="mt-3 text-xs text-faint-c">
                            Зазвичай це триває 5–15 секунд. Далі — кеш на 12 годин, наступного разу буде миттєво.
                        </p>
                    </div>
                </div>
            </div>

            {/* Skeleton list — імітує реальні картки */}
            <div className="space-y-3" aria-hidden="true">
                {[0, 100, 200, 300, 400, 500].map((delay) => (
                    <SkeletonCard key={delay} delay={delay} />
                ))}
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default RecommendationsLoadingState;
